import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  REVIEW_DECISION,
  type ReviewDecision,
  type ReviewPayload,
} from '@/components/common/ReviewDecision';
import { useRefreshRelatedData } from '@/hooks/useRefreshRelatedData';
import { showApiErrorToast } from '@/utils/errors';
import {
  approveInstituteSubmission,
  instituteSubmissionQueryKeys,
  rejectInstituteSubmission,
} from '../api/instituteSubmissions.api';

interface ReviewArgs {
  id: number;
  decision: ReviewDecision;
  payload: ReviewPayload;
}

const REVIEW_MESSAGE: Record<ReviewDecision, { success: string; failure: string }> = {
  APPROVE: {
    success: 'Submission approved',
    failure: 'Failed to approve the submission',
  },
  REJECT: {
    success: 'Submission rejected',
    failure: 'Failed to reject the submission',
  },
};

export function useReviewInstituteSubmission(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const refreshRelatedData = useRefreshRelatedData();

  return useMutation({
    mutationFn: ({ id, decision, payload }: ReviewArgs) =>
      decision === REVIEW_DECISION.APPROVE
        ? approveInstituteSubmission(id, payload)
        : rejectInstituteSubmission(id, payload),
    onSuccess: (submission, { id, decision }) => {
      queryClient.setQueryData(instituteSubmissionQueryKeys.detail(id), submission);
      void refreshRelatedData('institute');
      toast.success(REVIEW_MESSAGE[decision].success);
      onSuccess?.();
    },
    onError: (error, { decision }) => {
      void refreshRelatedData('institute');
      showApiErrorToast(error, REVIEW_MESSAGE[decision].failure);
    },
  });
}
